<template>
  <DashboardCard :border="false" :firstContent="true">
    <template #content>
      <section class="goals">
        <div class="col gauge-col with-separator pr-30px d-flex flex-column justify-content-between">
          <h5 class="sub-title mb-4">{{ $t('widgets.tireGoals.title') }}</h5>
          <span>{{ $t('widgets.tireGoals.subtitle') }}</span>
          <div class="mt-4">
            <span>
              <b>
                {{ $t('widgets.tireGoals.achievementsTitle') }}
              </b>
            </span>
            <br />
            <span class="fs-16px">
              {{ $t('widgets.tireGoals.achievementsSubtitle') }}
              <b>{{ commitment }}</b>
              {{ $t('widgets.tireGoals.achievementsSubtitle2') }}
            </span>
          </div>
          <div class="mt-3 gauge-chart-wrap">
            <GaugeChart :remaining="achieved" :target="commitment" :achieved="achieved"
              :goal-label="$t('widgets.tireGoals.performanceGoalProgress')"
              titleClass="text-normal font-28 mb-0" />
          </div>
        </div>

        <div class="your-points with-separator col">
          <span class="points-intro">{{ $t('widgets.tireGoals.cta') }}</span>

          <div class="mt-4">
            <b>{{ $t('widgets.tireGoals.pointsTitle') }}</b>
          </div>

          <span>
            {{ $t('widgets.tireGoals.pointsSubtitle') }}
          </span>


          <div class="points-data fw-bold d-flex align-items-center justify-content-between">
            <div class="points-block flex-1">
              <p class="fs-3 mb-1">{{ $filters.formatNumberWithCommas(pointsByPurchases) }}</p>
              <p class="fw-bold mb-0">{{ $t('widgets.tireGoals.pointsEarnedPurchases') }}</p>
            </div>

            <div class="p-relative d-inline-block h-100 mx-3">
              <div class="separator"></div>
            </div>

            <div class="points-block flex-1">
              <p class="fs-3 mb-1">{{ $filters.formatNumberWithCommas(pointsByBoosters) }}</p>
              <p class="fw-bold mb-0">{{ $t('widgets.tireGoals.pointsEarnedBoosters') }}</p>
            </div>
          </div>
        </div>

        <div class="col charts-col d-flex flex-column">
          <div class="section-title mx-3 mt-4">
            <h6>{{ $t('widgets.tireGoals.purchasesDetail') }}</h6>
          </div>

          <div class="charts-row flex-grow-1 mt-2">
            <div class="chart-card by-brand flex-grow-1 mt-3">
              <MDBTable borderless class="mini-legend table-dashboard">
                <tbody>
                  <tr v-for="(b, i) in brandSeries" :key="i" class="border">
                    <td>
                      {{ b.label }}
                    </td>
                    <td class="text-end">{{ b.percent }}</td>
                  </tr>
                </tbody>
              </MDBTable>
              <div class="p-1 d-flex justify-content-center border rounded-circle shadow-circle mt-4"
                style="width: 150px; height: 150px;">
                <Doughnut :chart-data="brandChartData" :chart-options="brandChartOptions" :plugins="[centerTextBrand]"
                  :styles="{ width: '140px', height: '140px' }" />
              </div>

            </div>

            <div class="p-relative d-inline-block h-100 mx-3">
              <div class="separator"></div>
            </div>

            <div class="chart-card per-rim mt-4">
              <div class="p-1 d-flex justify-content-center border rounded-circle shadow-circle">
                <Doughnut :chart-data="rimChartData" :chart-options="rimChartOptions" :plugins="[centerTextRim]"
                  :styles="{ width: '140px', height: '140px' }" />
              </div>



              <MDBTable borderless class="mini-legend table-dashboard">
                <tbody>
                  <tr v-for="(label, i) in rimLabels" :key="i" class="border">
                    <td>
                      <span class="legend-circle" :style="{ backgroundColor: rimColors[i] }"></span>
                      {{ label }}
                    </td>
                    <td class="text-end">{{ rimPercents[i] }}</td>
                  </tr>
                </tbody>
              </MDBTable>
            </div>
          </div>
        </div>
      </section>
    </template>
  </DashboardCard>
</template>

<script setup>
import DashboardCard from "../User/Wholesaler/DashboardCard.vue";
import { MDBTable } from "mdb-vue-ui-kit";
import { computed, defineProps } from "vue";
import { Doughnut } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { createCenterTextPlugin } from "../../utils/chartPlugins";
import { useI18n } from "vue-i18n";
import GaugeChart from "../Shared/GaugeChart.vue";

ChartJS.register(Title, Tooltip, Legend, ArcElement);
const { t } = useI18n();

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});

const commitment = computed(() => Number(props.data?.tire_achievement_goal.goal ?? 2000));
const achieved = computed(() => Number(props.data?.tire_achievement_goal.achievement ?? 1200));

const pointsByPurchases = computed(() => Number(props.data?.points?.points_earned_by_purchases ?? 0));
const pointsByBoosters = computed(() => Number(props.data?.points?.points_earned_by_boosters ?? 0));

const rimLabels = ["< 15”", "16”", "17”", "18”", "> 19”"];
const rimColors = ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF"];
const rimValues = computed(() => [
  Number(props.data?.rim?.inch_15_or_less ?? 0),
  Number(props.data?.rim?.inch_16 ?? 0),
  Number(props.data?.rim?.inch_17 ?? 0),
  Number(props.data?.rim?.inch_18 ?? 0),
  Number(props.data?.rim?.inch_19_or_more ?? 0),
]);

const rimPercents = computed(() => [
  props.data?.rim?.inch_15_or_less_percent,
  props.data?.rim?.inch_16_percent,
  props.data?.rim?.inch_17_percent,
  props.data?.rim?.inch_18_percent,
  props.data?.rim?.inch_19_or_more_percent,
]);

const brandSeries = computed(() => {
  const raw = props.data?.brand || {};

  const brands = [
    { key: "firestone", label: "Firestone" },
    { key: "bridgestone", label: "Bridgestone" },
  ];

  return brands.map(b => ({
    label: b.label,
    percent: Number(raw[`${b.key}_percent`] ?? 0),
    value: Number(raw[b.key] ?? 0),
  }));
});

const rimChartData = computed(() => ({
  labels: rimLabels,
  datasets: [
    {
      data: rimValues.value,
      backgroundColor: rimColors,
      borderColor: "#fff",
      borderWidth: 2,
      spacing: 1,
      cutout: "70%",
    },
  ],
}));
const rimChartOptions = {
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const idx = ctx.dataIndex;
          const value = Number(ctx.raw ?? 0);
          const pct = rimPercents.value[idx] ?? 0;
          return ` ${ctx.label}: ${value.toLocaleString()} (${pct}%)`;
        },
      },
    },
  },
};

const brandChartData = computed(() => ({
  labels: brandSeries.value.map(b => b.label),
  datasets: [
    {
      data: brandSeries.value.map(b => b.value || b.percent || 0),
      backgroundColor: brandColors.value,
      borderColor: "#fff",
      borderWidth: 2,
      spacing: 1,
      cutout: "70%",
    },
  ],
}));
const brandChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const idx = ctx.dataIndex;
          const label = brandSeries.value[idx]?.label || ctx.label;
          const pct = brandSeries.value[idx]?.percent || 0;
          const val = Number(ctx.raw ?? 0);
          return ` ${label}: ${val.toLocaleString()} (${pct}%)`;
        },
      },
    },
  },
};

const brandColors = computed(() => {
  const base = ["#E63946", "#1D3557", "#457B9D", "#A8DADC", "#2A9D8F", "#E9C46A", "#F4A261", "#E76F51"];
  return brandSeries.value.map((_, i) => base[i % base.length]);
});

const centerTextRim = createCenterTextPlugin(t("widgets.rim.centerTitle"), {
  color: "#000",
  weight: "700",
  divisor: 12,
});

const centerTextBrand = createCenterTextPlugin(t("widgets.brand.centerTitle"), {
  color: "#000",
  weight: "700",
  divisor: 12,
});

</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/table_dashboard.scss";

.charts-row {
  display: flex;
}

.goals {
  display: flex;
}

.gauge-chart-wrap {
  width: min(100%, 290px);
  max-width: 100%;
  margin-inline: auto;
}

@media (max-width: $xxxlBreakpoint) {
  .gauge-chart-wrap {
    width: min(100%, 240px);
  }
}

@media (max-width: $xxlBreakpoint) {
  .gauge-chart-wrap {
    width: min(100%, 170px);
  }
}

.points-data {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex: 1;
}

.your-points {
  font-size: 16px;
  font-weight: normal;
  padding: 0 18px;
  display: flex;
  flex-direction: column;
}

.points-intro {
  display: block;
  line-height: 1.5;
  margin-top: calc(1.25rem + 1.5rem);
}

.with-separator {
  position: relative;
}

.with-separator::after {
  content: "";
  position: absolute;
  right: 0;
  top: 0px;
  bottom: 0px;
  width: 1px;
  background-color: #B3B3B3;
}

.pr-30px {
  padding-right: 30px;
}

.by-brand {
  padding: 0 25px;
  font-size: 12px !important;
}

.per-rim {
  padding-left: 15px;
  font-size: 12px !important;
}

.font-45px {
  font-size: 45px;
}

.mini-legend {
  font-size: 12px !important;
}

.separator {
  position: absolute;
  top: 16px;
  bottom: 0;
  right: 0;
  width: 1px;
  background-color: #B3B3B3;
}

.legend-circle {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
}

.fs-16px {
  font-size: 15px;
}

.fs-14px {
  font-size: 14px;
}

.text-width {
  white-space: pre-line;
}
</style>